// Shared PDF chrome for CourtScribe court documents (summary + transcript):
// Judicial Service of Ghana letterhead with logo, page banners, footer with the
// draft label / transcript ID / page numbers, and the low-level PdfWriter used by
// both renderers. Keeping this in one place guarantees the transcript and the
// summary look identical.
import jsPDF from 'jspdf';
import logoUrl from '../../assets/images/logos/court-letterhead.svg';

export const PAGE = { width: 210, height: 297, margin: 15 };
export const CONTENT_WIDTH = PAGE.width - PAGE.margin * 2;
export const BOTTOM = PAGE.height - 22;
export const LABEL_WIDTH = 62;

export const COLORS = {
  ink: [17, 24, 39],
  muted: [107, 114, 128],
  line: [156, 163, 175],
  bannerBg: [229, 231, 235],
  labelBg: [243, 244, 246],
  headBg: [219, 223, 229],
  accent: [79, 70, 229],
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// jsPDF cannot embed SVG directly; rasterize the brand logo once per export.
export const getLogoDataUrl = async () => {
  try {
    const img = await loadImage(logoUrl);
    const scale = 4;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, img.width * scale || 180 * scale);
    canvas.height = Math.max(1, img.height * scale || 152 * scale);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
};

export class PdfWriter {
  constructor(doc) {
    this.doc = doc;
    this.y = 0;
    this.page = 1;
    this.meta = null;
  }

  startPage(meta) {
    this.meta = meta;
    const doc = this.doc;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.muted);
    doc.setFontSize(8);
    if (this.page === 1) {
      doc.setDrawColor(...COLORS.line);
      doc.setLineWidth(0.8);
      doc.line(PAGE.margin, 12, PAGE.width - PAGE.margin, 12);
      this.y = 16;
    } else {
      doc.text(
        `${meta.caseTitle} — Suit No.: ${meta.suitNumber}`,
        PAGE.margin,
        14,
        { maxWidth: CONTENT_WIDTH }
      );
      doc.setDrawColor(...COLORS.line);
      doc.setLineWidth(0.3);
      doc.line(PAGE.margin, 17, PAGE.width - PAGE.margin, 17);
      this.y = 22;
    }
  }

  newPage() {
    this.doc.addPage();
    this.page += 1;
    this.startPage(this.meta);
  }

  ensure(space) {
    if (this.y + space > BOTTOM) this.newPage();
  }

  finish() {
    const doc = this.doc;
    const total = doc.getNumberOfPages();
    for (let i = 1; i <= total; i += 1) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.ink);
      doc.text(this.meta.draftLabel, PAGE.margin, PAGE.height - 10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.muted);
      doc.text(
        `Transcript ID: ${this.meta.transcriptId}`,
        PAGE.width / 2,
        PAGE.height - 10,
        { align: 'center', maxWidth: 90 }
      );
      doc.text(`Page ${i} of ${total}`, PAGE.width - PAGE.margin, PAGE.height - 10, { align: 'right' });
      doc.setDrawColor(...COLORS.line);
      doc.setLineWidth(0.3);
      doc.line(PAGE.margin, PAGE.height - 14, PAGE.width - PAGE.margin, PAGE.height - 14);
    }
  }

  text(value, { size = 10, style = 'normal', align = 'left', color = COLORS.ink, gap = 2 } = {}) {
    const doc = this.doc;
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
    // jsPDF anchors text at x for the given alignment: left edge, page centre, or
    // right edge. Anchoring centred lines at the margin would push them off-page.
    const anchorX =
      align === 'center' ? PAGE.width / 2 : align === 'right' ? PAGE.width - PAGE.margin : PAGE.margin;
    const lines = doc.splitTextToSize(String(value ?? ''), CONTENT_WIDTH);
    const lineHeight = size * 0.45 + 1;
    lines.forEach((line) => {
      this.ensure(lineHeight + 1);
      doc.text(line, anchorX, this.y, { align, maxWidth: CONTENT_WIDTH });
      this.y += lineHeight;
    });
    this.y += gap;
  }

  partBanner(title) {
    this.ensure(14);
    const doc = this.doc;
    const height = 8;
    doc.setFillColor(...COLORS.bannerBg);
    doc.rect(PAGE.margin, this.y, CONTENT_WIDTH, height, 'F');
    doc.setDrawColor(...COLORS.line);
    doc.setLineWidth(0.3);
    doc.rect(PAGE.margin, this.y, CONTENT_WIDTH, height);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.ink);
    doc.text(title, PAGE.margin + 3, this.y + 5.5);
    this.y += height + 4;
  }

  rowLines(cells, widths, size) {
    const doc = this.doc;
    doc.setFontSize(size);
    const wrapped = cells.map((cell, i) => doc.splitTextToSize(String(cell ?? ''), widths[i] - 4));
    const rows = Math.max(...wrapped.map((lines) => lines.length));
    const lineHeight = size * 0.45 + 0.8;
    return { wrapped, rows, lineHeight, height: rows * lineHeight + 3 };
  }

  drawRow(cells, widths, { boldFirst = false, fill = null, size = 9 } = {}) {
    const doc = this.doc;
    let { wrapped, rows, lineHeight, height } = this.rowLines(cells, widths, size);
    this.ensure(height);
    const maxRows = Math.max(1, Math.floor((BOTTOM - this.y - 3) / lineHeight));
    if (rows > maxRows) {
      wrapped = wrapped.map((lines) => lines.slice(0, maxRows));
      wrapped = wrapped.map((lines, i) => {
        if (lines.length === maxRows && String(cells[i] ?? '').length > lines.join(' ').length) {
          return [...lines.slice(0, maxRows - 1), `${lines[maxRows - 1]} …`];
        }
        return lines;
      });
      rows = maxRows;
      height = rows * lineHeight + 3;
    }
    const top = this.y;
    let x = PAGE.margin;
    if (fill) {
      doc.setFillColor(...fill);
      doc.rect(x, top, CONTENT_WIDTH, height, 'F');
    }
    wrapped.forEach((lines, i) => {
      doc.setFont('helvetica', boldFirst && i === 0 ? 'bold' : 'normal');
      doc.setFontSize(size);
      doc.setTextColor(...COLORS.ink);
      doc.text(lines, x + 2, top + lineHeight, { maxWidth: widths[i] - 3 });
      doc.setDrawColor(...COLORS.line);
      doc.setLineWidth(0.2);
      doc.rect(x, top, widths[i], height);
      x += widths[i];
    });
    this.y = top + height;
  }

  labelTable(rows) {
    const widths = [LABEL_WIDTH, CONTENT_WIDTH - LABEL_WIDTH];
    rows.forEach(([label, value]) =>
      this.drawRow([label, value], widths, { boldFirst: true, fill: null })
    );
    this.y += 3;
  }

  headTable(head, dataRows, widths) {
    this.drawRow(head, widths, { boldFirst: true, fill: COLORS.headBg });
    dataRows.forEach((cells) => this.drawRow(cells, widths, {}));
    this.y += 3;
  }

  bullets(items, { size = 9 } = {}) {
    const doc = this.doc;
    items.forEach((item) => {
      const lines = doc.splitTextToSize(`•  ${item}`, CONTENT_WIDTH - 6);
      const lineHeight = size * 0.45 + 0.8;
      this.ensure(lines.length * lineHeight + 2);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(size);
      doc.setTextColor(...COLORS.ink);
      doc.text(lines, PAGE.margin + 4, this.y + lineHeight * 0.8);
      this.y += lines.length * lineHeight + 1;
    });
    this.y += 2;
  }

  subHeading(title, { size = 10 } = {}) {
    this.ensure(8);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(size);
    this.doc.setTextColor(...COLORS.ink);
    this.doc.text(title, PAGE.margin, this.y + size * 0.45);
    this.y += size * 0.5 + 2.5;
  }

  // One conversation turn: a timestamp gutter on the left, the speaker in bold and
  // the utterance text on the right, with a hairline separator beneath. Long turns
  // flow across page breaks.
  conversationTurn({ speaker, time, text }) {
    const doc = this.doc;
    const size = 9.5;
    const lineHeight = size * 0.45 + 1;
    const gutter = 22;
    const bodyX = PAGE.margin + gutter;
    const bodyWidth = CONTENT_WIDTH - gutter - 2;
    const textLines = doc.splitTextToSize(String(text ?? ''), bodyWidth);

    // Keep the speaker header with at least one line of text on the page.
    this.ensure(lineHeight * 2 + 4);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.muted);
    doc.text(String(time ?? ''), PAGE.margin, this.y + lineHeight);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(size);
    doc.setTextColor(...COLORS.accent);
    doc.text(String(speaker ?? 'Speaker'), bodyX, this.y + lineHeight, { maxWidth: bodyWidth });
    this.y += lineHeight;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(size);
    doc.setTextColor(...COLORS.ink);
    textLines.forEach((line) => {
      this.ensure(lineHeight + 1);
      doc.text(line, bodyX, this.y + lineHeight);
      this.y += lineHeight;
    });

    // Separator (skip if we just rolled to a fresh page mid-turn).
    if (this.y + 4 <= BOTTOM) {
      doc.setDrawColor(...COLORS.line);
      doc.setLineWidth(0.1);
      doc.line(PAGE.margin, this.y + 2, PAGE.margin + CONTENT_WIDTH, this.y + 2);
    }
    this.y += 6;
  }
}

// Letterhead shared by the summary and the transcript: logo, JUDICIAL SERVICE OF
// GHANA, court/division, the document title and the review notice.
export const renderLetterhead = (writer, doc, meta, logoDataUrl) => {
  const centerX = PAGE.width / 2;
  if (logoDataUrl) {
    // court-letterhead.svg is 180x152; scale height to preserve that aspect.
    const logoWidth = 30;
    const logoHeight = logoWidth * (152 / 180);
    doc.addImage(logoDataUrl, 'PNG', centerX - logoWidth / 2, writer.y, logoWidth, logoHeight);
    writer.y += logoHeight + 3;
  }
  writer.ensure(30);
  writer.text('JUDICIAL SERVICE OF GHANA', { size: 13, style: 'bold', align: 'center' });
  writer.text(
    meta.courtName && meta.courtName !== 'Not stated'
      ? meta.courtName.toUpperCase()
      : 'COURT OF APPEAL DIVISION',
    { size: 11, style: 'bold', align: 'center' }
  );
  writer.text(meta.documentTitle, { size: 11, style: 'bold', align: 'center' });
  writer.text(meta.bannerNotice, { size: 8, style: 'italic', align: 'center', color: COLORS.muted });
  writer.y += 2;
  doc.setDrawColor(...COLORS.line);
  doc.setLineWidth(0.3);
  doc.line(PAGE.margin, writer.y, PAGE.width - PAGE.margin, writer.y);
  writer.y += 5;
};

export { jsPDF };
